import { cn } from '@up2stay/up2promo-ui';
import { type ImgHTMLAttributes, useState } from 'react';

interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /**
   * Fallback image source if the main image fails to load
   */
  fallbackSrc?: string;
  /**
   * Whether to show a loading placeholder
   */
  showLoadingPlaceholder?: boolean;
}

/**
 * A simple Image component wrapper that provides consistent styling
 * and error handling. Works with any image source including external URLs.
 */
export function Image(props: ImageProps) {
  return <ImageInner key={props.src ?? ''} {...props} />;
}

function ImageInner({
  src,
  alt,
  className,
  fallbackSrc,
  showLoadingPlaceholder = false,
  onError,
  ...props
}: ImageProps) {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (fallbackSrc && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc);
      setHasError(false);
    } else {
      setHasError(true);
      onError?.(e);
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {isLoading && showLoadingPlaceholder && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
      <img
        src={imgSrc}
        alt={alt}
        className={cn(
          'h-full w-full object-cover',
          isLoading && showLoadingPlaceholder && 'opacity-0',
          hasError && 'opacity-50',
        )}
        onError={handleError}
        onLoad={handleLoad}
        {...props}
      />
    </div>
  );
}
